import WidgetCard from "@/components/cards/widget-card";
import { CustomTooltip } from "@/components/charts/custom-tooltip";
import { useMedia } from "@/hooks/use-media";
import { Order, OrderStatus } from "@/types";
import SimpleBar from "@/ui/simplebar";
import {
    Area,
    AreaChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { Progressbar } from "rizzui";

export default function DeliveredOrders({
    orders,
    className,
}: {
    orders?: Order[];
    className?: string;
}) {
    const isTablet = useMedia("(max-width: 800px)", false);

    const getPendingOrdersByMonth = (month: number) =>
        orders?.filter(
            (order) => new Date(order.createdDate).getMonth() === month,
        ).length ?? 0;

    const data = [
        { month: "Jan", delivered: getPendingOrdersByMonth(0) },
        { month: "Feb", delivered: getPendingOrdersByMonth(1) },
        { month: "Mar", delivered: getPendingOrdersByMonth(2) },
        { month: "Apr", delivered: getPendingOrdersByMonth(3) },
        { month: "May", delivered: getPendingOrdersByMonth(4) },
        { month: "Jun", delivered: getPendingOrdersByMonth(5) },
        { month: "Jul", delivered: getPendingOrdersByMonth(6) },
        { month: "Aug", delivered: getPendingOrdersByMonth(7) },
        { month: "Sep", delivered: getPendingOrdersByMonth(8) },
        { month: "Oct", delivered: getPendingOrdersByMonth(9) },
        { month: "Nov", delivered: getPendingOrdersByMonth(10) },
        { month: "Dec", delivered: getPendingOrdersByMonth(11) },
    ];

    const totalOrders = orders?.length ?? 0;

    const deliveredOrders =
        orders?.filter((order) => order.status === OrderStatus.Delivered)
            .length ?? 0;

    return (
        <WidgetCard
            title="Delivered Orders"
            description={`${deliveredOrders} orders delivered`}
            titleClassName="font-normal text-gray-500 text-sm sm:text-sm 2xl:text-base font-inter"
            descriptionClassName="text-xl font-semibold text-gray-900 mt-1.5 font-lexend 2xl:text-2xl"
            className={className}
        >
            <div className="mt-5">
                <p className="text-base">
                    {totalOrders > 0
                        ? ((deliveredOrders / totalOrders) * 100).toFixed(2)
                        : 0}
                    % Delivered
                </p>
                <Progressbar
                    value={(deliveredOrders / totalOrders) * 100}
                    rounded="md"
                    color="primary"
                    aria-label={"Progress bar"}
                    className="mt-2 h-5"
                />
                <p className="mt-4">
                    Pending orders:{" "}
                    <span className="font-semibold text-gray-900">
                        {totalOrders - deliveredOrders}
                    </span>
                </p>
            </div>
            <div className="mt-5">
                <p>Delivered Orders since:</p>
                <SimpleBar className="mt-4 h-40">
                    <div className="h-40 w-full">
                        <ResponsiveContainer
                            width="100%"
                            height="100%"
                            {...(isTablet && { minWidth: "700px" })}
                        >
                            <AreaChart
                                data={data}
                                margin={{
                                    left: -30,
                                }}
                                className="[&_.recharts-cartesian-axis-tick-value]:fill-gray-500 rtl:[&_.recharts-cartesian-axis.yAxis]:-translate-x-12 [&_.recharts-cartesian-grid-vertical]:opacity-0"
                            >
                                <defs>
                                    <linearGradient
                                        id="delivered"
                                        x1="0"
                                        y1="0"
                                        x2="0"
                                        y2="1"
                                    >
                                        <stop
                                            offset="5%"
                                            stopColor="#eab308"
                                            stopOpacity={0.12}
                                        />
                                        <stop
                                            offset="95%"
                                            stopColor="#ffffff"
                                            stopOpacity={0}
                                        />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid
                                    strokeDasharray="8 10"
                                    strokeOpacity={0.435}
                                />
                                <XAxis
                                    dataKey="month"
                                    axisLine={false}
                                    tickLine={false}
                                    className=" "
                                />
                                <YAxis tickLine={false} />
                                <Tooltip content={<CustomTooltip />} />
                                <Area
                                    type="monotone"
                                    dataKey="delivered"
                                    stroke="#eab308"
                                    strokeWidth={2}
                                    fill="url(#pending)"
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </SimpleBar>
            </div>
        </WidgetCard>
    );
}
